import React, { useEffect, useMemo, useState } from "react";
import { X, Users, Radio, Film, Clock, Globe, User, UserX, Activity, RefreshCw, Monitor } from "lucide-react";

const RANGES = [
  { key: "24h", label: "24H" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "all", label: "ALL TIME" },
];

function humanDuration(seconds) {
  if (!seconds || seconds < 60) return `${Math.round(seconds || 0)}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

function sinceLabel(value) {
  if (!value) return "2.3.69-r1.3";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "2.3.69-r1.3";
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
}

export function OwnerStats({ open, onClose, apiBase, authHeaders, channels, videosByChannel }) {
  const [range, setRange] = useState("24h");
  const [summary, setSummary] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);

  const channelById = useMemo(() => {
    const m = new Map();
    (channels || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [channels]);

  const videoById = useMemo(() => {
    const m = new Map();
    for (const [chId, list] of Object.entries(videosByChannel || {})) {
      if (Array.isArray(list)) for (const v of list) m.set(v.id, { ...v, __channelId: chId });
    }
    return m;
  }, [videosByChannel]);

  const refresh = React.useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        fetch(`${apiBase}/stats/summary?range=${range}`, {
          credentials: "include", headers: { ...(authHeaders || {}) },
        }).then((r) => (r.ok ? r.json() : null)),
        fetch(`${apiBase}/stats/live`, {
          credentials: "include", headers: { ...(authHeaders || {}) },
        }).then((r) => (r.ok ? r.json() : null)),
      ]);
      setSummary(s);
      setLive(l);
    } catch (_) { /* retain last snapshot */ }
    finally { setLoading(false); }
  }, [open, apiBase, authHeaders, range]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const iv = setInterval(async () => {
      try {
        const l = await fetch(`${apiBase}/stats/live`, {
          credentials: "include", headers: { ...(authHeaders || {}) },
        }).then((r) => (r.ok ? r.json() : null));
        if (l) setLive(l);
      } catch (_) { /* noop */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [open, apiBase, authHeaders]);

  if (!open) return null;

  const hours = (summary && summary.hours) || Array(24).fill(0);
  const maxHour = Math.max(1, ...hours);
  const allTime = range === "all";

  return (
    <div className="absolute top-12 right-0 w-[700px] max-h-[85vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_24px_rgba(85,255,85,0.25)] z-50" data-testid="owner-stats-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Activity size={16} /><div className="text-[13px] tracking-[0.28em]">┌─ ★ ACCURATE STATS ─┐</div></div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} title="Refresh" className="opacity-70 hover:opacity-100"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
          <button onClick={onClose}><X size={16} /></button>
        </div>
      </div>

      <div className="border border-[var(--phosphor)] p-3 mb-3 flex items-center justify-between">
        <div><div className="text-[10px] tracking-[0.22em] opacity-70">LIVE NOW · CONNECTED VIEWERS</div><div className="text-4xl font-semibold tabular-nums">{live ? live.count : "—"}</div></div>
        <div className="text-right text-[10px] opacity-65 leading-snug">AUTHORITATIVE LOBBY WEBSOCKET<br />OWNER CONNECTIONS EXCLUDED</div>
      </div>

      <div className="flex items-center gap-1 mb-2">
        {RANGES.map((r) => <button key={r.key} onClick={() => setRange(r.key)} className={"flex-1 text-[11px] tracking-[0.18em] py-1 border " + (range === r.key ? "border-[var(--phosphor)] bg-[var(--phosphor)] text-black" : "border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] hover:border-[var(--phosphor)]")}>{r.label}</button>)}
      </div>

      <div className="mb-3 border border-[#FFCC00]/70 text-[#FFCC00] px-2 py-1.5 text-[10px] tracking-[0.12em]" data-testid="owner-stats-accuracy-epoch">
        {allTime ? "ALL TIME" : range.toUpperCase()} · TRUSTWORTHY V3 DATA FROM {sinceLabel(summary && summary.data_since).toUpperCase()}
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        <StatCard icon={<Users size={10} />} label="UNIQUE VIEWERS" value={summary ? summary.unique_visitors : "—"} />
        <StatCard icon={<Monitor size={10} />} label="SESSIONS" value={summary ? summary.sessions : "—"} />
        <StatCard icon={<User size={10} />} label="SIGNED IN" value={summary ? summary.signed_in : "—"} />
        <StatCard icon={<UserX size={10} />} label="ANONYMOUS" value={summary ? summary.anonymous : "—"} />
      </div>

      <div className="border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2 mb-3">
        <div className="text-[10px] tracking-[0.22em] opacity-70 flex items-center gap-1 mb-2"><Clock size={10} /> HOURS ACTIVE · UTC</div>
        <div className="flex items-end gap-[2px] h-16">{hours.map((h, i) => <div key={i} className="flex-1 bg-[color-mix(in_srgb,var(--phosphor)_45%,transparent)]" style={{ height: `${Math.round((h / maxHour) * 100)}%`, minHeight: h > 0 ? "2px" : "1px" }} title={`${i}:00 UTC — ${h} measured events`} />)}</div>
        <div className="flex justify-between text-[9px] opacity-60 mt-1"><span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span></div>
      </div>

      <StatsTable title="TOP CHANNELS" icon={<Radio size={10} />} empty="No measured channel activity in this accuracy epoch.">
        {summary && summary.top_channels.map((row) => {
          const ch = channelById.get(row.channel_id);
          return <tr key={row.channel_id} className="border-t border-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"><td className="py-1">{ch ? `${ch.numberLabel || ch.number} · ${ch.name}` : String(row.channel_id || "").slice(0, 12)}</td><td className="text-right">{row.tunes} tunes</td><td className="text-right">{row.views} views</td><td className="text-right">{humanDuration(row.watch_seconds)}</td></tr>;
        })}
      </StatsTable>

      <StatsTable title="TOP TAPES" icon={<Film size={10} />} empty="No measured tape activity in this accuracy epoch.">
        {summary && summary.top_tapes.map((row) => {
          const v = videoById.get(row.video_id);
          const ch = channelById.get(row.channel_id);
          return <tr key={`${row.video_id}:${row.channel_id || ""}`} className="border-t border-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"><td className="py-1">{v ? v.title : String(row.video_id || "").slice(0, 12)}{ch && <span className="opacity-55"> · {ch.name}</span>}</td><td className="text-right">{row.views} views</td><td className="text-right">{humanDuration(row.watch_seconds)}</td></tr>;
        })}
      </StatsTable>

      <StatsTable title="TOP REFERRERS" icon={<Globe size={10} />} empty="No inbound referrers in this accuracy epoch.">
        {summary && summary.top_referrers.map((row) => <tr key={row.host} className="border-t border-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"><td className="py-1">{row.host}</td><td className="text-right">{row.count}</td></tr>)}
      </StatsTable>

      <div className="text-[10px] opacity-65 leading-snug">
        RAW DETAIL RETENTION: 30 DAYS · V3 BROWSER-HASH DEDUP · SESSIONS SEPARATE · TUNES STABLE FOR 750ms · VIEWS COUNT ONCE PER TAPE ENTRY · WATCH TIME COUNTS CONFIRMED PLAYBACK ONLY · OWNER TRAFFIC EXCLUDED.
        {summary && ` · ${summary.total_events} V3 EVENTS`}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return <div className="border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2"><div className="text-[9px] tracking-[0.13em] opacity-70 flex items-center gap-1">{icon} {label}</div><div className="text-xl font-semibold tabular-nums">{value}</div></div>;
}

function StatsTable({ title, icon, empty, children }) {
  const rows = React.Children.toArray(children).filter(Boolean);
  return <div className="border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2 mb-3"><div className="text-[10px] tracking-[0.22em] opacity-70 flex items-center gap-1 mb-2">{icon} {title}</div>{rows.length === 0 ? <div className="text-[11px] opacity-55 py-1">{empty}</div> : <table className="w-full text-[11px]"><tbody>{rows}</tbody></table>}</div>;
}
